import React, { useState } from 'react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { VariableDetails, VariableRateType, RateChange } from '../../types/mortgageTypes';
import { calculateMonthlyRate, calculateMonthlyPayment } from '../../utils/mortgageCalculations';

interface VariableRateSectionProps {
  variableDetails: VariableDetails;
  onChange: (details: VariableDetails) => void;
  country: 'USA' | 'Canada';
  principal: number;
  amortizationMonths: number;
  firstMonthlyPayment?: number;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const PRIME_INCREASES = [0, 1, 2, 3];

export const VariableRateSection: React.FC<VariableRateSectionProps> = ({
  variableDetails,
  onChange,
  country,
  principal,
  amortizationMonths,
  firstMonthlyPayment,
}) => {
  const [forecastOpen, setForecastOpen] = useState(false);

  const handleChange = (field: keyof VariableDetails, value: VariableDetails[keyof VariableDetails]) => {
    onChange({
      ...variableDetails,
      [field]: value,
    });
  };

  // Rate change schedule handlers
  const rateChanges: RateChange[] = variableDetails.rateChanges ?? [];

  const handleAddRateChange = () => {
    const lastYear = rateChanges.length > 0 ? rateChanges[rateChanges.length - 1].year + 1 : 2;
    const amortizationYears = Math.round(amortizationMonths / 12);
    const nextYear = Math.min(lastYear, amortizationYears);
    onChange({
      ...variableDetails,
      rateChanges: [...rateChanges, { year: nextYear, delta: 0.25 }],
    });
  };

  const handleUpdateRateChange = (index: number, field: keyof RateChange, value: number) => {
    const updated = rateChanges.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    );
    onChange({ ...variableDetails, rateChanges: updated });
  };

  const handleRemoveRateChange = (index: number) => {
    const updated = rateChanges.filter((_, i) => i !== index);
    onChange({ ...variableDetails, rateChanges: updated });
  };

  // Compute the effective prime rate at each forecasted year (for the preview column)
  const effectivePrimeAtYear = (upToIndex: number): number => {
    let prime = variableDetails.primeRate;
    for (let i = 0; i <= upToIndex; i++) {
      prime += rateChanges[i].delta;
    }
    return prime;
  };

  const currentRate = variableDetails.primeRate - variableDetails.discount;

  // Compute monthly payment at a given annual rate
  const paymentAtRate = (annualRate: number): number => {
    if (principal <= 0 || amortizationMonths <= 0) return 0;
    const mr = calculateMonthlyRate(annualRate, country, 'variable');
    return calculateMonthlyPayment(principal, mr, amortizationMonths);
  };

  const basePayment = paymentAtRate(currentRate);

  // VRM trigger rate: the rate at which the fixed monthly payment no longer covers interest
  // trigger_rate = (payment / principal) × 12 × 100
  const triggerRate =
    country === 'Canada' &&
    variableDetails.variableType === 'vrm' &&
    firstMonthlyPayment &&
    firstMonthlyPayment > 0 &&
    principal > 0
      ? (firstMonthlyPayment / principal) * 12 * 100
      : null;

  const primeRiseToTrigger =
    triggerRate !== null ? triggerRate - currentRate : null;

  return (
    <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-md">
      <h4 className="font-semibold text-teal-900 mb-3">
        {country === 'Canada' ? 'Variable Rate Details (Canadian)' : 'Variable Rate Details'}
      </h4>

      {country === 'Canada' && (
        <div className="mb-4">
          <Select
            label="Variable Type"
            value={variableDetails.variableType}
            onChange={(value) => handleChange('variableType', value as VariableRateType)}
            options={[
              { value: 'arm', label: 'ARM - Adjustable Payment (payment changes with rate)' },
              { value: 'vrm', label: 'VRM - Fixed Payment (amortization adjusts)' },
            ]}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="max-w-[280px]">
          <Input
            label="Prime Rate"
            type="number"
            value={variableDetails.primeRate}
            onChange={(e) => handleChange('primeRate', Number(e.target.value))}
            suffix="%"
          />
          <p className="text-xs text-teal-700 mt-1">
            {country === 'Canada' ? 'Current Bank of Canada prime rate' : 'Current prime rate'}
          </p>
        </div>

        <div className="max-w-[280px]">
          <Input
            label="Discount from Prime"
            type="number"
            value={variableDetails.discount}
            onChange={(e) => handleChange('discount', Number(e.target.value))}
            suffix="%"
          />
          <p className="text-xs text-teal-700 mt-1">
            Negative values = premium (Prime + X%)
          </p>
        </div>

        <div className="max-w-[280px]">
          <label className="block text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-1.5">
            Current Rate
          </label>
          <div className="relative rounded-lg shadow-sm mb-2">
            <div className="block w-full rounded-lg border border-stone-border bg-stone-50 py-2 px-3 pr-10 text-sm text-ink select-none">
              {currentRate.toFixed(3)}
            </div>
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-ink-faint sm:text-sm">%</span>
            </div>
          </div>
          <p className="text-xs text-teal-700 mt-1">
            Your rate will fluctuate with changes to the prime rate.
          </p>
        </div>
      </div>

      {country === 'Canada' && (variableDetails.variableType === 'vrm' || variableDetails.variableType === 'arm') && (
        <div className="mt-4 p-3 bg-teal-100 rounded-md">
          {variableDetails.variableType === 'vrm' && (
            <p className="text-xs text-teal-800">
              <strong>VRM Note:</strong> Your payment stays fixed, but the amount going to principal vs interest
              will adjust. If rates rise significantly, you may hit the "trigger rate" where your payment
              doesn't cover all the interest.
            </p>
          )}
          {variableDetails.variableType === 'arm' && (
            <p className="text-xs text-teal-800">
              <strong>ARM Note:</strong> Your payment will adjust immediately when the prime rate changes,
              providing more predictable amortization but less payment stability.
            </p>
          )}
        </div>
      )}

      {/* Forecast Prime Rate Changes - collapsed by default */}
      <div className="mt-4 border border-indigo-200 rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => setForecastOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-3 bg-indigo-100 hover:bg-indigo-200 text-indigo-900 text-sm font-semibold transition-colors"
        >
          <span>Forecast Prime Rate Changes</span>
          <div className="flex items-center gap-2">
            {rateChanges.length > 0 && (
              <span className="text-xs font-normal bg-indigo-700 text-white rounded-full px-2 py-0.5">
                {rateChanges.length} change{rateChanges.length !== 1 ? 's' : ''} configured
              </span>
            )}
            <ChevronRightIcon
              className={`h-4 w-4 transition-transform ${forecastOpen ? 'rotate-90' : ''}`}
            />
          </div>
        </button>

        {forecastOpen && (
          <div className="p-4 bg-white">
            <p className="text-xs text-gray-600 mb-4">
              Model how your prime rate is expected to change over the life of the mortgage.
              Each row applies from the start of that year onwards. Changes are cumulative -
              adding +0.25% in year 2 and +0.25% in year 3 means a total of +0.50% from year 3.
              Leave blank to use a constant prime rate.
            </p>

            {rateChanges.length > 0 && (
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-indigo-50 text-indigo-900">
                      <th className="text-left px-3 py-2 font-semibold">Starting Year</th>
                      <th className="text-left px-3 py-2 font-semibold">Rate Change</th>
                      <th className="text-left px-3 py-2 font-semibold">Resulting Prime</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rateChanges.map((change, i) => {
                      const effectivePrime = effectivePrimeAtYear(i);
                      const effectiveRate = effectivePrime - variableDetails.discount;
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-indigo-50'}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500 mr-1">Year</span>
                              <input
                                type="number"
                                min={2}
                                max={Math.round(amortizationMonths / 12)}
                                value={change.year}
                                onChange={(e) =>
                                  handleUpdateRateChange(i, 'year', Number(e.target.value))
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step={0.05}
                                value={change.delta}
                                onChange={(e) =>
                                  handleUpdateRateChange(i, 'delta', Number(e.target.value))
                                }
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                              />
                              <span className="text-xs text-gray-500">%</span>
                              {change.delta > 0 && (
                                <span className="text-xs text-orange-600 font-medium">↑</span>
                              )}
                              {change.delta < 0 && (
                                <span className="text-xs text-green-600 font-medium">↓</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            <span className="font-medium">{effectivePrime.toFixed(2)}%</span>
                            <span className="text-xs text-gray-500 ml-1">
                              (your rate: {effectiveRate.toFixed(2)}%)
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => handleRemoveRateChange(i)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                              title="Remove"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              onClick={handleAddRateChange}
              className="flex items-center gap-1 text-sm text-indigo-700 hover:text-indigo-900 font-medium transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Add rate change
            </button>

            {rateChanges.length > 0 && (
              <p className="text-xs text-gray-400 mt-3">
                The amortization schedule and Key Insights will reflect these forecasted rate
                changes. The sensitivity table below shows static scenarios at today's rate level.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Rate Sensitivity Table */}
      {principal > 0 && amortizationMonths > 0 && (
        <div className="mt-4">
          <h5 className="text-sm font-semibold text-teal-900 mb-2">
            Rate Sensitivity - What if prime rises?
          </h5>
          <p className="text-xs text-teal-700 mb-3">
            This table shows how your{' '}
            {country === 'Canada' && variableDetails.variableType === 'vrm'
              ? 'amortization and interest exposure change'
              : 'monthly payment changes'}{' '}
            at different prime rate levels.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-teal-200 text-teal-900">
                  <th className="text-left px-3 py-2 rounded-tl-md font-semibold">Prime Change</th>
                  <th className="text-left px-3 py-2 font-semibold">Your Rate</th>
                  <th className="text-left px-3 py-2 font-semibold">Monthly Payment</th>
                  <th className="text-left px-3 py-2 rounded-tr-md font-semibold">Change vs Today</th>
                </tr>
              </thead>
              <tbody>
                {PRIME_INCREASES.map((increase) => {
                  const scenarioRate = currentRate + increase;
                  const payment = paymentAtRate(scenarioRate);
                  const diff = payment - basePayment;
                  const isTriggerCrossed =
                    triggerRate !== null && scenarioRate >= triggerRate;
                  const rowClass = isTriggerCrossed
                    ? 'bg-red-50 border-l-2 border-red-400'
                    : increase === 0
                    ? 'bg-white'
                    : 'bg-teal-50';

                  return (
                    <tr key={increase} className={rowClass}>
                      <td className="px-3 py-2 font-medium text-gray-800">
                        {increase === 0 ? (
                          <span className="text-teal-700">No change</span>
                        ) : (
                          <span className={isTriggerCrossed ? 'text-red-700 font-semibold' : 'text-gray-700'}>
                            +{increase}%
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{scenarioRate.toFixed(2)}%</td>
                      <td className="px-3 py-2 font-semibold text-gray-900">
                        {formatCurrency(payment)}
                        {isTriggerCrossed && (
                          <span className="ml-2 text-xs font-normal text-red-600">⚠ trigger</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {increase === 0 ? (
                          <span className="text-gray-500">-</span>
                        ) : (
                          <span className="text-orange-700 font-medium">
                            +{formatCurrency(diff)}/mo
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VRM Trigger Rate Warning (Canada only) */}
      {triggerRate !== null && primeRiseToTrigger !== null && (
        <div
          className={`mt-4 p-3 rounded-md border ${
            primeRiseToTrigger <= 1
              ? 'bg-red-50 border-red-300'
              : primeRiseToTrigger <= 2
              ? 'bg-orange-50 border-orange-300'
              : 'bg-amber-50 border-amber-300'
          }`}
        >
          <p
            className={`text-sm font-semibold ${
              primeRiseToTrigger <= 1
                ? 'text-red-800'
                : primeRiseToTrigger <= 2
                ? 'text-orange-800'
                : 'text-amber-800'
            }`}
          >
            {primeRiseToTrigger <= 1 ? '🚨' : primeRiseToTrigger <= 2 ? '⚠️' : 'ℹ️'} VRM Trigger
            Rate: {triggerRate.toFixed(2)}%
          </p>
          <p
            className={`text-xs mt-1 ${
              primeRiseToTrigger <= 1
                ? 'text-red-700'
                : primeRiseToTrigger <= 2
                ? 'text-orange-700'
                : 'text-amber-700'
            }`}
          >
            Prime would need to rise by{' '}
            <strong>{primeRiseToTrigger.toFixed(2)}%</strong> (from {variableDetails.primeRate}% to{' '}
            {(variableDetails.primeRate + primeRiseToTrigger).toFixed(2)}%) to reach your trigger rate.
            If crossed, your lender may require a lump-sum payment or higher monthly payments.
          </p>
        </div>
      )}
    </div>
  );
};
